import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { Coins, Loader2, Users, Trash2, Plus, ArrowRight, X, Clock, MapPin, Crosshair, Send, Check, AlertCircle, DollarSign } from "lucide-react";

const APR_ICON = "https://legends.ml/static/images/lib/item/05050000.png";
const ONYX_APPLE_ICON = "https://royals-library.netlify.app/images/items/02022179.png";

export default function CreateBountyModal({
  showCreateBounty, setShowCreateBounty, bountyForm, setBountyForm,
  AREA_BOSSES, handleCreateBounty, actionLoading, formatMesos
}) {
  return (
      <Dialog open={showCreateBounty} onOpenChange={setShowCreateBounty}>
        <DialogContent className="bg-slate-900 border-amber-500/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Create Bounty</DialogTitle>
            <DialogDescription className="text-amber-200/60">
              Post a bounty for hunters to find and kill your target boss
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-amber-200">Boss</Label>
              <Select value={bountyForm.boss_name} onValueChange={(v) => setBountyForm({ ...bountyForm, boss_name: v })}>
                <SelectTrigger className="bg-black/40 border-gray-600">
                  <SelectValue placeholder="Select a boss" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {AREA_BOSSES.map((boss) => (
                    <SelectItem key={boss} value={boss}>{boss}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-amber-200">Reward (in millions of mesos)</Label>
              <div className="relative">
                <Input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="e.g., 5"
                  value={bountyForm.reward}
                  onChange={(e) => setBountyForm({ ...bountyForm, reward: e.target.value })}
                  className="bg-black/40 border-gray-600 pr-24"
                  data-testid="bounty-reward-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-amber-400/70 pointer-events-none">
                  {bountyForm.reward ? formatMesos(bountyForm.reward) : "M mesos"}
                </span>
              </div>
            </div>

            {/* Alternative Reward: APR & Onyx Apple */}
            <div className="p-3 rounded-lg space-y-2" style={{ background: "rgba(168,85,247,0.08)", border: "1px solid rgba(168,85,247,0.25)" }}>
              <Label className="text-purple-300 text-xs font-semibold">Alternative / Additional Reward</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <img src={APR_ICON} alt="APR" className="w-5 h-5" />
                    <span className="text-purple-200 text-xs">APR</span>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Amount"
                    value={bountyForm.reward_apr_amount || ""}
                    onChange={(e) => setBountyForm({ ...bountyForm, reward_apr_amount: e.target.value })}
                    className="bg-black/40 border-purple-500/40 text-white h-8 text-sm"
                    data-testid="bounty-apr-amount"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <img src={ONYX_APPLE_ICON} alt="Onyx Apple" className="w-5 h-5" />
                    <span className="text-purple-200 text-xs">Onyx Apple</span>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Amount"
                    value={bountyForm.reward_onyx_apple_amount || ""}
                    onChange={(e) => setBountyForm({ ...bountyForm, reward_onyx_apple_amount: e.target.value })}
                    className="bg-black/40 border-purple-500/40 text-white h-8 text-sm"
                    data-testid="bounty-onyx-amount"
                  />
                </div>
              </div>
              {(bountyForm.reward_apr_amount || bountyForm.reward_onyx_apple_amount) && bountyForm.reward && (
                <p className="text-[10px] text-purple-300/60 italic">Items + mesos combined as reward</p>
              )}
            </div>

            <div>
              <Label className="text-amber-200">Contact IGNs (at least 1 required)</Label>
              {[0, 1, 2].map((i) => (
                <Input
                  key={i}
                  placeholder={`IGN ${i + 1}`}
                  value={bountyForm.contact_igns[i]}
                  onChange={(e) => {
                    const newIgns = [...bountyForm.contact_igns];
                    newIgns[i] = e.target.value;
                    setBountyForm({ ...bountyForm, contact_igns: newIgns });
                  }}
                  className="bg-black/40 border-gray-600 mt-2"
                />
              ))}
            </div>

            <div>
              <Label className="text-amber-200">Duration: {bountyForm.duration_minutes} minutes</Label>
              <Slider
                value={[bountyForm.duration_minutes]}
                onValueChange={(v) => setBountyForm({ ...bountyForm, duration_minutes: v[0] })}
                min={30}
                max={720}
                step={30}
                className="mt-2"
              />
              <p className="text-xs text-gray-400 mt-1">
                {Math.floor(bountyForm.duration_minutes / 60)}h {bountyForm.duration_minutes % 60}m
              </p>
            </div>

            <div>
              <Label className="text-amber-200">Custom Message (Optional)</Label>
              <Textarea
                placeholder="Add a note for hunters..."
                value={bountyForm.message}
                onChange={(e) => setBountyForm({ ...bountyForm, message: e.target.value })}
                className="bg-black/40 border-gray-600"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateBounty(false)}>Cancel</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-black" onClick={handleCreateBounty}>
              Post Bounty
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
